<template>
  <div
    class="zoox-component zoox-mobile-layout"
    :class="{'hide-header': hidden, 'with-suboptions': subOptions.length}"
  >
    <z-header
      with-logo
      is-mobile
      class="header"
      :class="{'has-suboption': subOptions.length}"
      :src-avatar="srcAvatar"
      :product="product"
      :lang="lang"
      :lang-options="langOptions"
      :username="username"
      :hide-on-scroll="hideOnScroll"
      :email="userEmail"
      :company="company"
      @logout="onClickLogout"
      @change-lang="onChangeLang"
    >
      <template v-if="subOptions.length" v-slot:extension>
        <v-tabs
          slider-color="primary"
          grow
          :value="activeRouteIndex"
          @change="onChangeNavigation"
        >
          <v-tab
            v-for="(option, index) of subOptions"
            :key="index"
            :disabled="option.disabled"
            @change="onChangeNavigation(index)"
          >
            {{ option.title }}
          </v-tab>
        </v-tabs>
      </template>

      <template v-if="$scopedSlots['product']" #product>
        <!--@slot Slot para customizar a area de produto do componente -->
        <slot name="product"></slot>
      </template>
      <template v-if="$scopedSlots['username']" #username>
        <slot name="username" />
      </template>
    </z-header>

    <div id="zoox-content" class="zoox-content overflow-y-auto">
      <!--@slot Slot do conteúdo principal do componente -->
      <slot name="content"></slot>
    </div>

    <z-tab-menu
      class="menu"
      :absolute="absolute"
      :class="{'menu-absolute': menuAbsolute}"
      :menu-options="menuOptions"
      @click="onClickOption"
    />
  </div>
</template>

<script>
/**
 * Componente de layout para navegação mobile. Costuma-se utilizar um header
 * comum e abas como subrotas, diretamente no layout.
 *
 * @displayName Mobile Layout
 */
export default {
  name: 'ZMobileLayout',
  props: {
    product: {
      type: String,
      required: true,
    },
    /**
     * Linguagens disponíveis no select. Utilizar somente
     * en-pt-es.
     */
    langOptions: {
      type: Array,
      required: false,
      default: () => ['pt', 'en'],
    },
    /**
     * Define as opções do menu lateral
     *
     * @typedef Object {title:string,icon:string,isActive:boolean,options:array}
     */
    menuOptions: {
      type: Array,
      required: true,
    },
    username: {
      type: String,
      required: true,
    },
    srcAvatar: {
      type: [String, Object],
      required: false,
    },
    lang: {
      type: String,
      required: true,
    },
    menuAbsolute: {
      type: Boolean,
      required: false,
      default: false,
    },
    activeRoute: {
      type: String,
      required: true,
    },
    hideTabNavigation: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideOnScroll: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Empresa que o produto está
     */
    company: {
      type: String,
      default: '',
    },
    /**
     * Email do usuário
     */
    userEmail: {
      type: String,
      default: '',
    },
  },
  data() {
    return {hidden: false}
  },
  computed: {
    subOptions() {
      const activeOption = this.menuOptions.find((option) => option.isActive)

      return activeOption && Array.isArray(activeOption.options)
        ? activeOption.options
        : []
    },
    activeRouteIndex() {
      if (this.subOptions.length) {
        return this.subOptions.findIndex(
          (option) => option.route === this.activeRoute
        )
      }
      return null
    },
  },
  methods: {
    onChangeLang(lang) {
      /**
       * Evento disparado quando o usuário altera a linguagem
       *
       * @property {string} lang - Linguagem selecionada
       */
      this.$emit('change-lang', lang)
    },

    onClickOption(option) {
      /**
       * Evento disparado quando o usuário clica em uma opção do menu
       *
       * @property {Object} option - Opção selecionada
       */
      this.$emit('click:option', option)
    },
    onChangeNavigation(index) {
      /**
       * Evento disparado quando o usuário clica em uma sub-rota do menu
       *
       * @property {Object} subOption - Sub-opção selecionada
       */
      this.$emit('click:sub', this.subOptions[index])
    },
    onClickLogout() {
      /**
       * Evento disparado quando o usuário clica no botão de logout
       */
      this.$emit('logout')
    },
  },
}
</script>

<style lang="scss">
.zoox-mobile-layout {
  .v-toolbar__extension {
    height: 35px !important;
  }

  .v-slide-group__prev,
  .v-slide-group__next {
    display: none !important;
  }

  .zoox-header {
    &.has-suboption {
      height: calc(48px + 35px) !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.zoox-mobile-layout {
  position: relative;
  height: 100%;
  overflow: hidden;
  min-width: 250px;
  background: var(--primary);

  .zoox-content {
    background-color: var(--background);
    overflow-y: auto;
    padding-bottom: 55px;
    padding-top: 48px;
    height: 100vh;
    max-height: 100vh;
  }

  &.with-suboptions {
    .zoox-content {
      padding-top: 83px;
    }
  }

  .menu {
    position: fixed;
    width: 100%;
    bottom: 0px;

    &.menu-absolute {
      position: absolute;
    }
  }
}
</style>
